import React, { useState } from "react";

const PaymentVerificationPage = () => {
  const [selectedFilter, setSelectedFilter] = useState("pending");

  // Mock data - replace with real API calls
  const paymentSlips = [
    {
      id: "PS-001",
      orderId: "ORD-001",
      customer: "John Doe",
      amount: 299.99,
      bankName: "Seylan Bank",
      accountHolder: "John Doe",
      accountNumber: "****7890",
      uploadedAt: "2024-01-15T10:30:00Z",
      status: "pending",
      slipUrl: "/api/placeholder/400/600",
    },
    {
      id: "PS-002",
      orderId: "ORD-003",
      customer: "Mike Johnson",
      amount: 89.99,
      bankName: "Commercial Bank",
      accountHolder: "Mike Johnson",
      accountNumber: "****1234",
      uploadedAt: "2024-01-13T09:15:00Z",
      status: "pending",
      slipUrl: "/api/placeholder/400/600",
    },
  ];

  const filteredSlips = paymentSlips.filter(
    (slip) => selectedFilter === "all" || slip.status === selectedFilter
  );

  const handleApprove = (slipId) => {
    console.log("Approving payment slip:", slipId);
    // Handle approval logic
  };

  const handleReject = (slipId) => {
    console.log("Rejecting payment slip:", slipId);
    // Handle rejection logic
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Payment Verification
        </h1>
        <p className="text-gray-600">
          Review and verify bank transfer payment slips
        </p>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label
          htmlFor="status"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Filter by Status
        </label>
        <select
          id="status"
          value={selectedFilter}
          onChange={(e) => setSelectedFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600"
        >
          <option value="pending">Pending Verification</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All Payments</option>
        </select>
      </div>

      {/* Payment Slips */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Payment Slips ({filteredSlips.length})
          </h2>
        </div>
        <div className="p-6">
          {filteredSlips.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredSlips.map((slip) => (
                <PaymentSlipCard
                  key={slip.id}
                  slip={slip}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No payment slips found.</p>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-2xl font-bold text-yellow-600">
            {paymentSlips.filter((p) => p.status === "pending").length}
          </div>
          <div className="text-sm text-gray-600">Pending Verification</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-2xl font-bold text-green-600">
            {paymentSlips.filter((p) => p.status === "approved").length}
          </div>
          <div className="text-sm text-gray-600">Approved Today</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-2xl font-bold text-gray-900">
            ${paymentSlips.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
          </div>
          <div className="text-sm text-gray-600">Total Amount</div>
        </div>
      </div>
    </div>
  );
};

const PaymentSlipCard = ({ slip, onApprove, onReject }) => {
  const [showSlip, setShowSlip] = useState(false);

  return (
    <>
      <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {slip.orderId}
            </h3>
            <p className="text-sm text-gray-600">{slip.customer}</p>
          </div>
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              slip.status === "pending"
                ? "bg-yellow-100 text-yellow-800"
                : slip.status === "approved"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {slip.status}
          </span>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Amount:</span>
            <span className="text-sm font-medium text-gray-900">
              ${slip.amount}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Bank:</span>
            <span className="text-sm font-medium text-gray-900">
              {slip.bankName}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Account Holder:</span>
            <span className="text-sm font-medium text-gray-900">
              {slip.accountHolder}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Account Number:</span>
            <span className="text-sm font-medium text-gray-900">
              {slip.accountNumber}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Uploaded:</span>
            <span className="text-sm font-medium text-gray-900">
              {new Date(slip.uploadedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => setShowSlip(true)}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 transition-colors"
          >
            View Slip
          </button>
          {slip.status === "pending" && (
            <>
              <button
                onClick={() => onApprove(slip.id)}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
              >
                Approve
              </button>
              <button
                onClick={() => onReject(slip.id)}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {/* Payment Slip Modal */}
      {showSlip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                Payment Slip - {slip.orderId}
              </h2>
              <button
                onClick={() => setShowSlip(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-gray-100 p-4 rounded-lg text-center">
                <p className="text-gray-600">
                  Payment slip image would be displayed here
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  In a real application, this would show the uploaded payment
                  slip image
                </p>
              </div>
            </div>

            {slip.status === "pending" && (
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    onApprove(slip.id);
                    setShowSlip(false);
                  }}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
                >
                  Approve Payment
                </button>
                <button
                  onClick={() => {
                    onReject(slip.id);
                    setShowSlip(false);
                  }}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
                >
                  Reject Payment
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentVerificationPage;
